import React, { useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import CheckboxField from 'components/CheckboxFieldGroup/CheckboxField';
import InputField from 'components/InputField';
import SelectField from 'components/SelectField';
import { Field, Form } from 'react-final-form';
import validations from 'validations';
import { useTranslation } from 'react-i18next';
import useValidations from 'router/subrouters/Auth/hooks/useValidations';
import MembersContext from 'router/subrouters/DataRoom/providers/Members/Members.context';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import { Button, Loader } from 'ncoded-component-library';
import ArrowrightIcon from 'icons/Arrowright.icon';
import { DataRoomMembers } from 'router/subrouters/DataRoom/models/DataRoomMember';
import useUserBasicInfo from 'hooks/useUserBasicInfo';
import EmailField from 'components/EmailField';

import './SingleAddMember.styles.scss';

export type AddBuyMemberForm = {
  firstName: string;
  lastName: string;
  email: string;
  addToExistingGroup?: boolean;
  dataRoomBuySideGroupId?: string;
  dataRoomBuySideGroupName?: string;
  grantFullAccess?: boolean;
};

type SingleAddMemberProps = {
  className?: string;
  updateMembers: (data: DataRoomMembers) => void;
};

const SingleAddMember: React.FC<SingleAddMemberProps> = (props) => {
  const { className, updateMembers } = props;

  const classes = classNames('single-add-member', className);

  const { buySideGroups } = useContext(MembersContext);
  const {
    dataRoom: { _id },
  } = useContext(DataRoomContext);
  const { popServerError, popSuccess } = useContext(PopNotificationsContext);

  const { isFetchingUserBasicInfo, prefilledFields, onEmailChange } =
    useUserBasicInfo();

  const commonValidations = useValidations();
  const { t } = useTranslation();

  const buySideGroupOptions = useMemo(
    () => buySideGroups.map(({ _id, name }) => ({ value: _id, label: name })),
    [buySideGroups],
  );

  const onSubmit = useCallback(
    async (values: AddBuyMemberForm) => {
      try {
        const {
          addToExistingGroup,
          dataRoomBuySideGroupId,
          dataRoomBuySideGroupName,
          ...rest
        } = values;

        const { data } = await dataRoomApi.members.addMember(_id, {
          ...rest,
          ...(addToExistingGroup
            ? { dataRoomBuySideGroupId }
            : { dataRoomBuySideGroupName }),
        });

        popSuccess(t('successfullyAddedBuySide'));
        updateMembers(data);
      } catch (e) {
        popServerError(e);
      }
    },
    [_id, popSuccess, t, updateMembers, popServerError],
  );

  return (
    <Form
      onSubmit={onSubmit}
      render={({ invalid, submitting, form, values, handleSubmit }) => (
        <form onSubmit={handleSubmit} className={classes}>
          <Field
            name="firstName"
            label={t('firstName')}
            component={InputField}
            required
            validate={commonValidations.name}
            disabled={prefilledFields.firstName}
          />

          <Field
            name="lastName"
            label={t('lastName')}
            component={InputField}
            required
            validate={commonValidations.name}
            disabled={prefilledFields.lastName}
          />

          <EmailField
            required
            onChangeHandle={(email) => onEmailChange({ email, form })}
          />

          {!!buySideGroups.length && (
            <Field
              name="addToExistingGroup"
              label={t('addToExistingGroup')}
              component={CheckboxField}
              type="checkbox"
            />
          )}

          <Field
            name="grantFullAccess"
            component={CheckboxField}
            type="checkbox"
            label={t('grantAccessToAllFiles')}
          />

          {values.addToExistingGroup ? (
            <Field
              name="dataRoomBuySideGroupId"
              label={t('buySideGroup')}
              component={SelectField}
              options={buySideGroupOptions}
              required
              validate={validations.required()}
            />
          ) : (
            <Field
              name="dataRoomBuySideGroupName"
              label={t('buySideGroup')}
              component={InputField}
            />
          )}

          <div className="actions">
            <Button
              type="submit"
              variant="outline"
              iconPosition="right"
              icon={ArrowrightIcon}
              disabled={invalid || isFetchingUserBasicInfo || submitting}
            >
              {t('sendInvite')}
            </Button>
          </div>

          {submitting && <Loader />}
        </form>
      )}
    />
  );
};

export default SingleAddMember;
